import { useAppSelector } from '../../lib/hooks';
import { ButtonGroup, IconButton, Pagination as ChakraPagination, Spinner } from '@chakra-ui/react';
import {
  useLazyGetNodeQuery,
  useAIsearchMutation,
  useSearchMutation,
} from '../../lib/api/GraphAPI';
import { DocumentResultType, DocumentType } from '../../lib/state/ViewTypes';
import fetchNodes from '../../lib/Search';
import { useEffect, useState } from 'react';
import { setDocumentResult } from '../../lib/state/slices/ProjectStateSlice';
import { useDispatch } from 'react-redux';
import { CSSProperties } from '@emotion/serialize';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { Node, Slice } from '../../lib/api/APITypes';

export default function Pagination(props: { style: CSSProperties }) {
  const dispatch = useDispatch();

  const currentSearchType: DocumentType = useAppSelector(
    (state) => state.viewReducer.documents.search.type,
  );
  const filter = useAppSelector((state) => state.viewReducer.documents.filter);
  const result = useAppSelector(
    (state) => state.viewReducer.documents.result[currentSearchType as keyof DocumentResultType],
  );

  const totalElements = result ? result.total_elements : 0;
  const pageSize = result ? result.page_size : 0;
  const page = result ? result.current_page : 0;

  const [isLoading, setIsLoading] = useState(false);

  const searchQueryContext = useAppSelector(
    (state) => state.viewReducer.documents.search.query.CONTEXT,
  );
  const searchQueryExact = useAppSelector(
    (state) => state.viewReducer.documents.search.query.EXACT,
  );

  const [getNodeTrigger] = useLazyGetNodeQuery();
  const [AIsearchTrigger, { isSuccess: contextIsSucess, data: contextData }] =
    useAIsearchMutation();

  const [searchTrigger, { isSuccess: exactIsSuccess, data: exactData }] = useSearchMutation();

  useEffect(() => {
    if (contextIsSucess && contextData) {
      setIsLoading(true);
      fetchNodes(
        contextData.data.ids.map((id: string) => id.split(':')[0] + ':' + id.split(':')[1]),
        getNodeTrigger,
      )
        .then((nodes) => {
          dispatch(
            setDocumentResult({
              type: 'CONTEXT',
              value: { ...contextData, data: nodes as unknown } as Slice<Node[]>,
            }),
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, [contextIsSucess, contextData, getNodeTrigger, dispatch]);

  useEffect(() => {
    // console.log('exactData', exactData);
    if (exactIsSuccess && exactData) {
      setIsLoading(true);
      fetchNodes(exactData.data.ids, getNodeTrigger)
        .then((nodes) => {
          dispatch(
            setDocumentResult({
              type: 'EXACT',
              value: { ...exactData, data: nodes as unknown } as Slice<Node[]>,
            }),
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, [exactIsSuccess, exactData, getNodeTrigger, dispatch]);

  return (
    <div style={{ ...props.style, display: 'flex', justifyContent: 'center' }}>
      <ChakraPagination.Root
        page={page + 1}
        count={totalElements ? totalElements : 0}
        defaultPage={1}
        pageSize={pageSize ? pageSize : 0}
        onPageChange={(details: any) => {
          // details: PageChangeDetails

          switch (currentSearchType) {
            case 'CONTEXT':
              setIsLoading(true);
              AIsearchTrigger({
                query: searchQueryContext,
                pagination: { pageNumber: details.page - 1, pageSize: pageSize },
                filter: filter,
              });
              break;
            case 'EXACT':
              setIsLoading(true);
              searchTrigger({
                query: searchQueryExact,
                filter: filter,
                pagination: {
                  pageNumber: details.page - 1,
                  pageSize: pageSize,
                },
              });
              break;
          }
        }}
      >
        <ButtonGroup
        >
          <ChakraPagination.PrevTrigger asChild>
            <IconButton
              background="cardDefault"
              color="secondaryText"
              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
              _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
            >
              <LuChevronLeft />
            </IconButton>
          </ChakraPagination.PrevTrigger>

          <ChakraPagination.Items
            color="secondaryText"
            background="cardDefault"
            render={(page) => (
              <IconButton
                _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
                variant={{ base: 'ghost', _selected: 'outline' }}
              >
                {isLoading ? <Spinner /> : page.value}
              </IconButton>
            )}
          />

          <ChakraPagination.NextTrigger asChild>
            <IconButton
              background="cardDefault"
              color="secondaryText"
              _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
              _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
            >
              <LuChevronRight />
            </IconButton>
          </ChakraPagination.NextTrigger>
        </ButtonGroup>
      </ChakraPagination.Root>
    </div>
  );
}
